import React from 'react';

import { AccountListPage } from '@vlabs/pages/accounts/AccountListPage';
import { FeaturesPage, InfoPage } from '@vlabs/pages/app';
import { ResetPasswordPage } from '@vlabs/pages/auth';
import { CardsAndListsPage } from '@vlabs/pages/cards-and-lists';
import { EditStreamPage as LunaStreamsEditStreamPage } from '@vlabs/pages/editStream/EditStreamPage';
import CreateHandlerPage from '@vlabs/pages/handlers/CreateHandlerPage';
import { EditHandlerPage } from '@vlabs/pages/handlers/EditHandlerPage';
import HandlerListPage from '@vlabs/pages/handlers/HandlerListPage';
import OtherScenarioStepper from '@vlabs/pages/handlers/preconfigured-scenarios/other/OtherScenarioStepper';
import { NotificationListPage, NotificationCreateEditPage } from '@vlabs/pages/notifications';
import {
  FacePage, ListDetailsPage, CardPage,
  VideoAnalyticListPage, VideoAnalyticDetailsPage,
  Workspace,
  VmsPage,
  EventDetailsPage,
  EventListPage,
} from '@vlabs/pages/point';
import { SearchPage } from '@vlabs/pages/search/SearchContainer';
import SourceListPage from '@vlabs/pages/sources/SourceListPage';
import { ChartSettings } from '@vlabs/pages/statistic/ChartSettings';
import { StatisticPage } from '@vlabs/pages/statistic/StatisticPage';
import { StreamPage } from '@vlabs/pages/stream/StreamPage';
import TaskListPage from '@vlabs/pages/tasks/TaskListPage';
import VideoAnalyticsStreamPage from '@vlabs/pages/video-analytics/streams/VideoAnalyticsStreamPage';
import VideoAnalytics from '@vlabs/pages/video-analytics/VideoAnalytics';
import { permissions } from '@vlabs/shared/config';
import withControl from '@vlabs/shared/hocs/routeControllerHOC';
import { VideoWall } from '@vlabs/shared/video-wall/VideoWall';
import { Pages } from '@vlabs/uikit';
import {
  generatePath, Redirect, Route, Switch,
} from 'react-router-dom';

// import { fetchAccounts } from '@vlabs/pages/accounts/store';
import { selectHandlerStateById } from '@vlabs/pages/handlers/selectors';
import { fetchHandlerList } from '@vlabs/pages/handlers/store';
import { selectLatestEventsState } from '@vlabs/pages/point/workspace/selectors';
import {
  selectAppConfig,
  selectAppServiceState,
  selectIsAppFeatureAvailable,
} from '@vlapps/point/src/app/store/selectors';

import { routes } from './routes';

const includedAttributes = ['gender', 'age', 'apparentGender', 'apparentAge', 'upperBody', 'lowerBody', 'headwear', 'backpack', 'map'];

const ControlledStreamPage = withControl({
  authRule: [permissions.videoanalytics.creation],
  componentState: (state) => (selectIsAppFeatureAvailable(state, 'components.video_analytics') ? 'loaded' : 'failed'),
})(VideoAnalyticsStreamPage);

function PointRouter(): React.JSX.Element {
  return (
    <Switch>
      {/* Map index */}
      {/* <Route path={routes.map.index} exact component={MapPage} /> */}

      {/* Event archive v2 */}
      <Route
        component={withControl({
          authRule: [permissions.event.view],
          includedAttributes,
        })(EventListPage)}
        exact
        path={routes.events.archive}
      />

      {/* CardsAndLists index */}
      <Route
        component={CardsAndListsPage}
        path={routes.cardsAndLists.activeTab}
      />
      <Redirect
        exact
        from={routes.cardsAndLists.index}
        to={generatePath(routes.cardsAndLists.activeTab, { activeTab: 'cards' })}
      />
      <Redirect
        exact
        from={routes.cards.index}
        to={generatePath(routes.cardsAndLists.activeTab, { activeTab: 'cards' })}
      />

      {/* VideoWall index */}
      <Route
        component={VideoWall}
        exact
        path={routes.videoWall.index}
      />

      {/* [App] Monitoring */}
      <Route
        component={withControl({
          authRule: [permissions.appInfo.view],
        })(InfoPage)}
        exact
        path={routes.app.info}
      />

      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(StatisticPage)}
        exact
        path={routes.events.statistic.all}
      />

      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(ChartSettings)}
        exact
        path={routes.events.statistic.read}
      />

      {/* [App] Features */}
      <Route
        component={withControl({
          authRule: [permissions.appInfo.view],
          showLicenseLink: false,
        })(FeaturesPage)}
        exact
        path={routes.app.features}
      />

      {/* VideoAnalytics */}
      <Route
        component={withControl({
          componentState: (state) => {
            const isAvailable = selectIsAppFeatureAvailable(state, 'components.video_analytics');
            return isAvailable ? 'loaded' : 'failed';
          },
        })(VideoAnalytics)}
        exact
        path={routes.videoAnalytics.all}
      />

      <Route
        component={withControl({
          componentState: (state) => {
            const isAvailable = selectIsAppFeatureAvailable(state, 'components.video_analytics');
            return isAvailable ? 'loaded' : 'failed';
          },
        })(VideoAnalyticListPage)}
        exact
        path={routes.videoAnalytics.archive}
      />

      <Route
        component={withControl({
          componentState: (state) => {
            const isAvailable = selectIsAppFeatureAvailable(state, 'components.video_analytics');
            return isAvailable ? 'loaded' : 'failed';
          },
        })(VideoAnalyticDetailsPage)}
        exact
        path={routes.videoAnalytics.details}
      />

      <Route
        exact
        path={routes.videoAnalytics.streams.edit}
        render={(router) => (
          <ControlledStreamPage
            streamId={router.match.params.streamId}
            {...router}
          />
        )}
      />

      {/* {/* [Clementine-auth] */}
      <Route
        component={ResetPasswordPage}
        path={routes.auth.reset}
      />
      <Route
        component={withControl({
          authRule: [permissions.account.view],
          componentState: (state) => selectAppServiceState(state, 'clementineAuth'),
          // onInit: ({ dispatch }) => dispatch(fetchAccounts),
        })(AccountListPage)}
        path={routes.accounts.all}
      />

      {/* [Luna] latest events (ws) */}
      <Route
        component={withControl({
          componentState: selectLatestEventsState,
          authRule: [permissions.event.view],
        })(Workspace)}
        exact
        path={routes.events.latest}
      />

      {/* Events-Details */}
      <Route
        component={withControl({
          authRule: [permissions.event.view],
        })(EventDetailsPage)}
        exact
        path={routes.events.eventDetails}
      />

      {/* [Luna] matching */}
      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
          authRule: [permissions.event.matching, permissions.face.matching],
          includedAttributes,
        })(SearchPage)}
        path={routes.search}
      />
      {/* Search index */}
      {/* <Route path={darksideRoutes.search.index} exact component={SearchPageDarkside} /> */}

      {/* [Luna] event sources */}
      <Route
        component={withControl({
          componentState: (state) => ([
            selectAppServiceState(state, 'lunaStreams'),
            selectAppServiceState(state, 'vlAccess'),
          ].find((v) => v === 'loaded') || 'failed'),
        })(SourceListPage)}
        exact
        path={routes.sources.main}
      />

      {/* [Luna streams] */}
      <Route
        exact
        path={routes.sources.lunaStreams.streams.create}
        render={(router) => {
          const Component = withControl({
            authRule: [permissions.source.creation],
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(LunaStreamsEditStreamPage);
          return (
            <Component
              afterCreateStream={(streamId) => {
                router.history.replace(generatePath(routes.sources.lunaStreams.streams.view, { streamId }));
              }}
              afterDeleteStream={() => {
                router.history.push(routes.sources.main);
              }}
              streamId="create"
              {...router}
            />
          );
        }}
      />
      <Route
        path={routes.sources.lunaStreams.streams.edit}
        render={(router) => {
          const Component = withControl({
            authRule: [permissions.source.modification],
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(LunaStreamsEditStreamPage);
          return (
            <Component
              afterCreateStream={(streamId) => {
                router.history.replace(generatePath(routes.sources.lunaStreams.streams.view, { streamId }));
              }}
              afterDeleteStream={() => {
                router.history.push(routes.sources.main);
              }}
              streamId={router.match.params.streamId}
              {...router}
            />
          );
        }}
      />
      <Route
        path={routes.sources.lunaStreams.streams.view}
        render={(router) => {
          const Component = withControl({
            authRule: [permissions.source.modification],
            componentState: (state) => selectAppServiceState(state, 'lunaStreams'),
          })(StreamPage);
          return (
            <Component
              afterCreateStream={(streamId) => {
                router.history.replace(generatePath(routes.sources.lunaStreams.streams.view, { streamId }));
              }}
              afterDeleteStream={() => {
                router.history.push(routes.sources.main);
              }}
              streamId={router.match.params.streamId}
              {...router}
            />
          );
        }}
      />

      {/* [Luna] handlers */}
      <Route
        component={withControl({
          authRule: [permissions.handler.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(OtherScenarioStepper)}
        exact
        path={routes.handlers.other}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.creation],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(CreateHandlerPage)}
        path={routes.handlers.create}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.view],
          componentState: (state, { match }) => (
            selectHandlerStateById(state, match?.params?.handlerId)
          ),
        })(EditHandlerPage)}
        path={routes.handlers.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.handler.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
          onInit: ({ dispatch }) => dispatch(fetchHandlerList),
        })(HandlerListPage)}
        exact
        path={routes.handlers.all}
      />

      {/* [Luna] lists */}
      <Route
        component={withControl({
          authRule: [permissions.face.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(ListDetailsPage)}
        path={routes.lists.read}
      />
      <Redirect
        exact
        from={routes.lists.all}
        to={generatePath(routes.cardsAndLists.activeTab, { activeTab: 'lists' })}
      />

      {/* [Luna] faces */}
      <Route
        component={withControl({
          authRule: [permissions.face.view],
        })(FacePage)}
        path={routes.facePage}
      />

      <Route
        component={withControl({
          authRule: [permissions.face.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(CardPage)}
        path={routes.externalId.read}
      />

      {/* [Luna] tasks */}

      <Route
        component={withControl({
          authRule: [permissions.task.view],
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(TaskListPage)}
        exact
        path={routes.tasks.all}
      />

      {/* [VLNotifier] */}
      <Route
        component={withControl({
          authRule: [permissions.notification.view],
          componentState: (state) => {
            const { uiNotificationsEnabled } = selectAppConfig(state);
            return uiNotificationsEnabled ? selectAppServiceState(state, 'vlNotifier') : 'failed';
          },
        })(NotificationCreateEditPage)}
        path={routes.notifications.read}
      />
      <Route
        component={withControl({
          authRule: [permissions.notification.view],
          componentState: (state) => {
            const { uiNotificationsEnabled } = selectAppConfig(state);
            return uiNotificationsEnabled ? selectAppServiceState(state, 'vlNotifier') : 'failed';
          },
        })(NotificationListPage)}
        exact
        path={routes.notifications.all}
      />

      {/* [VMS] */}
      <Route
        component={withControl({
          componentState: (state) => selectAppServiceState(state, 'lunaPlatform'),
        })(VmsPage)}
        path={routes.sources.vms.main}
      />

      {/* Router settings */}
      <Route
        exact
        path="/"
        render={() => <Redirect to={routes.events.latest} />}
      />
      <Route
        component={Pages.NotFoundPage}
        path="*"
      />
    </Switch>
  );
}

export { PointRouter };
